import React from 'react';
import TrackableModule from 'components/analytics/TrackableModule';
import { ModuleList, ModuleTypeName, ModuleTypes, getModuleSectionName, isMappedModule } from 'utils/modules';
import { ContentfulCollection } from 'libs/contentful/types';
import { PageContext } from 'utils/pageContext';
import { PageProps } from 'libs/contentful/queries/page/props';
import { ContentfulModule } from 'libs/contentful/types';
import { NinetailedExperience } from 'components/shared/Ninetailed/NinetailedExperience';
import { NinetailedMode } from 'types/constants';
import { getRoundedCornersNeighborExtend } from 'utils/styles';
import { ResolvedVisualSettings } from '@/utils/pageHelpers/visualSettings';
import { Document, Block, Inline, Text } from '@contentful/rich-text-types';
import { ModuleRenderer } from 'libs/experimentation/ModuleRenderer';
import { ExposureTracker } from 'libs/experimentation/ExposureTracker';
import { injectModuleProps, type RenderableModule } from 'libs/experimentation/propInjection';
import { hasNinetailedExperiences, isPersonalizedEntry } from 'libs/experimentation/type-guards';
import { isModuleRegistryTypeName } from 'libs/experimentation/moduleRegistry';

/**
 * Extracts plain text from a Contentful rich text document.
 * Recursively traverses the document tree to collect all text content.
 */
function extractTextFromRichText(document: Document | { json: Document } | undefined): string {
  if (!document) return '';

  // Handle wrapped documents (e.g., { json: Document })
  const doc = 'json' in document ? document.json : document;

  if (!doc?.content) return '';

  const extractText = (nodes: Array<Block | Inline | Text>): string => {
    return nodes
      .map(node => {
        if ('value' in node && typeof node.value === 'string') {
          return node.value;
        }
        if ('content' in node && Array.isArray(node.content)) {
          return extractText(node.content);
        }
        return '';
      })
      .join('');
  };

  return extractText(doc.content).trim();
}

/**
 * Gets the headline text from a module if it has a headline field.
 * Different modules may have the headline in different fields.
 */
function getModuleHeadline(module: ModuleTypes[ModuleTypeName]): string {
  const moduleWithHeadline = module as ModuleTypes[ModuleTypeName] & {
    headline?: Document | { json: Document };
    richTextHeadline?: Document | { json: Document };
  };

  // Try standard headline field first
  if (moduleWithHeadline.headline) {
    return extractTextFromRichText(moduleWithHeadline.headline);
  }

  // Some modules like ImpactHeadline use richTextHeadline
  if (moduleWithHeadline.richTextHeadline) {
    return extractTextFromRichText(moduleWithHeadline.richTextHeadline);
  }

  return '';
}

export interface ModuleCollectionRendererProps<T extends PageProps = PageProps> {
  modulesCollection: ContentfulCollection<ModuleTypes[ModuleTypeName]>;
  allowedModules: ModuleList | undefined;
  headers?: PageContext;
  page?: T;
  ninetailedMode?: NinetailedMode;
  visualSettings?: ResolvedVisualSettings;
  enrollmentUrl: string;
}

function getTrackingAttributes(
  module: ModuleTypes[ModuleTypeName],
  moduleIndex: number,
  headers?: PageContext,
  page?: PageProps,
) {
  return {
    moduleTitle: getModuleSectionName(module) || 'Untitled Section',
    moduleName: getModuleHeadline(module),
    moduleIndex: moduleIndex.toString(),
    moduleLocation: headers?.url || '',
    moduleType: module.__typename,
    pageTitle: page?.title || 'Untitled Page',
    pageTags: page?.contentfulMetadata?.tags || [],
  };
}

const isRenderableModule = (module: ModuleTypes[ModuleTypeName]): module is RenderableModule =>
  isModuleRegistryTypeName(module.__typename);

interface ModuleWithChildModules {
  modulesCollection: ContentfulCollection<ModuleTypes[ModuleTypeName]>;
}

const hasChildModulesCollection = (
  module: ModuleTypes[ModuleTypeName],
): module is ModuleTypes[ModuleTypeName] & ModuleWithChildModules =>
  'modulesCollection' in module && !!(module as ModuleWithChildModules).modulesCollection;

function filterModules(
  modules: ModuleTypes[ModuleTypeName][],
  allowedModules: ModuleList | undefined,
): ModuleTypes[ModuleTypeName][] {
  if (!modules) return [];
  return modules.filter(module => isMappedModule(module, allowedModules));
}

/**
 * Renders a collection of modules from an allowed list,
 * wrapping each module with tracking capabilities.
 */
const ModuleCollectionRenderer = <T extends PageProps = PageProps>({
  modulesCollection,
  allowedModules,
  headers,
  page,
  ninetailedMode,
  visualSettings,
  enrollmentUrl,
}: ModuleCollectionRendererProps<T>) => {
  const MODULE_INDEX_START = 1;

  if (!modulesCollection?.items) {
    return null;
  }

  const modules = filterModules(modulesCollection.items, allowedModules);
  const context = { enrollmentUrl, visualSettings };

  return (
    <>
      {modules.map((module, i) => {
        if (!isRenderableModule(module)) {
          return null;
        }

        const moduleIndex = i + MODULE_INDEX_START;

        // A module adjacent to a rounded-corner module extends its own background behind that
        // module's corners (see `data-rc-extend` in app/globals.css). This is the one place that
        // knows sibling order, so adjacency is resolved here and flagged on the universal wrapper.
        const rcExtend = getRoundedCornersNeighborExtend(modules[i - 1], modules[i + 1]);

        // In client (live-preview) mode the Ninetailed adapter is skipped in
        // resolvePage, so Ninetailed-experiment modules need the NinetailedExperience
        // wrapper to activate the JS plugin client-side. Statsig-only modules
        // continue through the v2 render path below.
        if (ninetailedMode === 'client' && hasNinetailedExperiences(module)) {
          const moduleNinetailedData = page?.ninetailedData?.get(module?.sys?.id);
          return (
            <TrackableModule
              key={moduleIndex}
              rcExtend={rcExtend}
              initialAttributes={getTrackingAttributes(module, moduleIndex, headers, page)}>
              <NinetailedExperience
                key={`section-${module?.sys?.id}`}
                entryId={module?.sys?.id}
                entry={module as ContentfulModule}
                hasExperiments={module.hasExperiments ?? false}
                ninetailedData={moduleNinetailedData}
              />
            </TrackableModule>
          );
        }

        const enriched = injectModuleProps(module, context);
        const exposure = isPersonalizedEntry(module) ? module._exposure : undefined;
        const children = hasChildModulesCollection(module) ? (
          <ModuleCollectionRenderer
            modulesCollection={module.modulesCollection}
            allowedModules={ModuleList.MAIN}
            headers={headers}
            page={page}
            ninetailedMode={ninetailedMode}
            enrollmentUrl={enrollmentUrl}
            visualSettings={visualSettings}
          />
        ) : undefined;

        return (
          <TrackableModule
            key={moduleIndex}
            rcExtend={rcExtend}
            initialAttributes={getTrackingAttributes(module, moduleIndex, headers, page)}>
            <ExposureTracker
              exposure={exposure}
              moduleId={module.sys?.id}
              nonce={headers?.nonce}
              hasPerformanceConsent={headers?.hasPerformanceConsent ?? true}>
              <ModuleRenderer module={enriched}>{children}</ModuleRenderer>
            </ExposureTracker>
          </TrackableModule>
        );
      })}
    </>
  );
};

export default ModuleCollectionRenderer;
